import React, { useMemo, memo } from 'react';
import { Clock } from 'lucide-react';
import { Brush, CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { PLAGES_PAR_FORMULE } from '../../pages/turpe/constants';
import ReactifDiagnosticBlock from '../ReactifDiagnosticBlock';

/**
 * Carte d'analyse pour UN PDL. Mémoïsée pour ne pas re-render à chaque frappe parent.
 * Les computations lourdes (sort, tan φ, agrégation horaire) sont cachées via useMemo
 * et n'invalident QUE si `tousLesPoints` change (i.e. à l'import seulement).
 */
const PDLAnalysisCardComponent = ({
  pdl,
  resultatPDL,
  pdlMetadonnees,
  pdlConfig,
  precoParPlage,
  precoUniqueCourbe,
  precoUniqueCourbeValidee,
  consoActuelleParPlage,
  formuleActuelle,
  visibiliteCourbes,
  setVisibiliteCourbes,
  zoomReset,
  setZoomReset,
  calculsAJour,
  calculsRapport,
  setPlagesHorairesModalPDL,
  hasCustomPlages,
  // Handlers
  handleSavePuissanceParPlage,
  handleSavePuissanceCourbe,
  handleSaveConsommationParPlage,
  handleSaveCommentaire,
  initialCommentaire,
  // Composants memoïsés du parent
  PuissanceInput,
  CommentaireInput,
  // Utilitaires
  agregerParHeure,
  calculerConsommationsParPlage,
}) => {
  const datasMois = resultatPDL.puissancesMaxParMois || [];
  const tousLesPoints = resultatPDL.tousLesPoints || [];
  const hasReactiveData = pdlMetadonnees.hasReactiveData !== undefined ? pdlMetadonnees.hasReactiveData : true;
  const pasTempsMinutes = pdlMetadonnees.pasTempsMinutes || 10;

  // ====== COMPUTATIONS LOURDES MEMOÏSÉES ======
  // tan φ global : iteration sur 50K+ points
  const tanPhiData = useMemo(() => {
    let totalKW = 0;
    let totalKVAR = 0;
    let hasReactive = false;
    tousLesPoints.forEach(p => {
      totalKW += p.kW || 0;
      if (p.kVAr !== undefined && p.kVAr > 0) {
        totalKVAR += p.kVAr;
        hasReactive = true;
      } else if (p.kVA !== undefined && p.kVA > 0 && p.kW !== undefined) {
        const kVar2 = (p.kVA * p.kVA) - (p.kW * p.kW);
        if (kVar2 > 0) {
          totalKVAR += Math.sqrt(kVar2);
          hasReactive = true;
        }
      }
    });
    const tanPhi = totalKW > 0 ? totalKVAR / totalKW : 0;
    return { tanPhi, hasReactive };
  }, [tousLesPoints]);

  // Monotone : sort + slice + map (le gros coût ~290ms par PDL !)
  const monotoneData = useMemo(() => {
    return tousLesPoints
      .slice()
      .sort((a, b) => (hasReactiveData ? (b.kVA || b.kW) : b.kW) - (hasReactiveData ? (a.kVA || a.kW) : a.kW))
      .slice(0, 2000)
      .map((p, idx) => ({
        index: idx,
        kW: p.kW,
        kVA: hasReactiveData ? p.kVA : null,
      }));
  }, [tousLesPoints, hasReactiveData]);

  // Agrégation horaire
  const dataChronologique = useMemo(() => agregerParHeure(tousLesPoints), [tousLesPoints, agregerParHeure]);
  const nbPointsHoraires = dataChronologique.length;

  // Stats : pmaxGlobale, moyenne (sur les mois — petit)
  const pmaxGlobale = useMemo(() => {
    if (datasMois.length === 0) return 0;
    return Math.max(...datasMois.map(m => m.pmax_kVA));
  }, [datasMois]);
  const moyennePmax = useMemo(() => {
    if (datasMois.length === 0) return 0;
    return datasMois.reduce((sum, m) => sum + m.pmax_kVA, 0) / datasMois.length;
  }, [datasMois]);

  // Nombre de jours uniques pour extrapolation annuelle
  const nbJoursUniques = useMemo(() => {
    const s = new Set();
    tousLesPoints.forEach(p => {
      const d = new Date(p.horodate);
      s.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
    });
    return s.size || 1;
  }, [tousLesPoints]);

  // Puissance souscrite max
  const puissancesActuellesParPlage = pdlConfig?.puissances_actuelles || {};
  const formule = pdlConfig?.formule_actuelle || '';
  const puissanceSouscrite = useMemo(() => Math.max(
    puissancesActuellesParPlage?.Unique || 0,
    puissancesActuellesParPlage?.HP || 0,
    puissancesActuellesParPlage?.HC || 0,
    puissancesActuellesParPlage?.Pointe || 0,
    puissancesActuellesParPlage?.HPH || 0,
    puissancesActuellesParPlage?.HCH || 0,
    puissancesActuellesParPlage?.HPB || 0,
    puissancesActuellesParPlage?.HCB || 0,
    puissancesActuellesParPlage?.max || 0
  ), [puissancesActuellesParPlage]);

  const plages = PLAGES_PAR_FORMULE[formule] || ['HPH', 'HCH', 'HPB', 'HCB'];
  const tanPhiOK = tanPhiData.tanPhi <= 0.4;

  if (datasMois.length === 0) return null;

  return (
    <div className="border border-[#BFC6CC] rounded-lg overflow-hidden">
      <div className="bg-[#397DFF] text-white px-6 py-4">
        <h3 className="font-heading font-semibold text-lg">PDL {pdl}</h3>
      </div>

      {/* Tableau puissances max par mois */}
      <div className="p-6">
        <h4 className="font-semibold text-[#191D1E] mb-4">Puissances Maximales par Mois</h4>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F6F9]">
              <tr>
                <th className="px-4 py-3 text-left text-[#687175]">Mois</th>
                <th className="px-4 py-3 text-right text-[#687175]">Pmax (kVA)</th>
                <th className="px-4 py-3 text-right text-[#687175]">Points mesure</th>
                <th className="px-4 py-3 text-center text-[#687175]">Qualité</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#BFC6CC]">
              {datasMois.map((mois, idx) => (
                <tr key={idx} className="hover:bg-[#F5F6F9]">
                  <td className="px-4 py-3 text-[#191D1E]">{mois.mois}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-[#397DFF]">
                    {mois.pmax_kVA.toFixed(1)}
                  </td>
                  <td className="px-4 py-3 text-right text-[#687175]">
                    {mois.nb_points.toLocaleString('fr-FR')}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      mois.qualite === 'Bon' ? 'bg-[#10D183]/20 text-[#10D183]' :
                      mois.qualite === 'Moyen' ? 'bg-[#F9C80E]/20 text-[#F9C80E]' :
                      'bg-red-100 text-red-600'
                    }`}>
                      {mois.qualite} ({mois.taux_complet.toFixed(0)}%)
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Stats globales */}
        <div className="mt-6 grid grid-cols-4 gap-4">
          <div className="p-4 bg-[#E8F4FD] rounded-lg text-center">
            <div className="text-sm text-[#687175]">Pmax Globale</div>
            <div className="text-2xl font-bold text-[#397DFF]">{pmaxGlobale.toFixed(1)} kVA</div>
          </div>
          <div className="p-4 bg-[#F5F6F9] rounded-lg text-center">
            <div className="text-sm text-[#687175]">Moyenne</div>
            <div className="text-2xl font-bold text-[#191D1E]">{moyennePmax.toFixed(1)} kVA</div>
          </div>
          <div className="p-4 bg-[#F5F6F9] rounded-lg text-center">
            <div className="text-sm text-[#687175]">Période</div>
            <div className="text-lg font-semibold text-[#191D1E]">{datasMois.length} mois</div>
          </div>
          {/* Tan φ */}
          {!tanPhiData.hasReactive ? (
            <div className="p-4 rounded-lg text-center bg-[#FFF7ED]">
              <div className="text-sm text-[#687175]">Tan φ global</div>
              <div className="text-lg font-bold text-[#F59E0B]">N/D</div>
              <div className="text-xs text-[#92400E]">Données réactives absentes</div>
            </div>
          ) : (
            <div className={`p-4 rounded-lg text-center ${tanPhiOK ? 'bg-[#E8FDF5]' : 'bg-[#FEF2F2]'}`}>
              <div className="text-sm text-[#687175]">Tan φ global</div>
              <div className={`text-2xl font-bold ${tanPhiOK ? 'text-[#10D183]' : 'text-[#E53935]'}`}>
                {tanPhiData.tanPhi.toFixed(3)}
              </div>
              <div className={`text-xs ${tanPhiOK ? 'text-[#10D183]' : 'text-[#E53935]'}`}>
                {tanPhiOK ? '✓ OK (≤0.4)' : '⚠ Réactive facturable'}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Graphiques Recharts */}
      <div className="p-6 space-y-8 bg-[#F5F6F9] border-t border-[#BFC6CC]">
        <div className="flex items-center gap-4 text-xs text-[#687175]">
          <span className="px-2 py-1 bg-white rounded border border-[#BFC6CC]">
            Pas de temps: <strong>{pasTempsMinutes} min</strong>
          </span>
          <span className={`px-2 py-1 rounded border ${hasReactiveData ? 'bg-green-50 border-green-200 text-green-700' : 'bg-yellow-50 border-yellow-200 text-yellow-700'}`}>
            Données réactives: <strong>{hasReactiveData ? 'Oui (kVA calculé)' : 'Non (kW uniquement)'}</strong>
          </span>
          {pdlMetadonnees?.hasInductiveData && pdlMetadonnees?.hasCapacitiveData && (
            <span className="px-2 py-1 rounded border bg-orange-50 border-orange-200 text-orange-700">
              <strong>PRI + PRC séparés</strong> (diagnostic réactif disponible)
            </span>
          )}
          {pdlMetadonnees?.hasInductiveData && !pdlMetadonnees?.hasCapacitiveData && (
            <span className="px-2 py-1 rounded border bg-blue-50 border-blue-200 text-blue-700">
              <strong>PRI uniquement</strong> (capacitif absent)
            </span>
          )}
        </div>

        {(pdlMetadonnees?.hasInductiveData || pdlMetadonnees?.hasCapacitiveData) && (
          <ReactifDiagnosticBlock
            pdl={pdl}
            tousLesPoints={tousLesPoints}
            pasTempsMinutes={pasTempsMinutes}
            domaineTension={pdlConfig?.tension}
            hasInductiveData={pdlMetadonnees?.hasInductiveData}
            hasCapacitiveData={pdlMetadonnees?.hasCapacitiveData}
          />
        )}

        {/* Monotone */}
        <div>
          <h4 className="font-semibold text-[#191D1E] mb-4">Monotone des Puissances (2000 points max)</h4>
          <div className="bg-white p-4 rounded-lg border border-[#BFC6CC]">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monotoneData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#BFC6CC" />
                <XAxis dataKey="index" label={{ value: 'Points triés par puissance décroissante', position: 'insideBottom', offset: -5 }} />
                <YAxis label={{ value: 'Puissance', angle: -90, position: 'insideLeft' }} />
                <Tooltip formatter={(value, name) => [typeof value === 'number' ? value.toFixed(2) : value, name]} />
                <Legend
                  onClick={(e) => setVisibiliteCourbes(prev => ({ ...prev, [e.dataKey]: !prev[e.dataKey] }))}
                  wrapperStyle={{ cursor: 'pointer' }}
                  formatter={(value, entry) => (
                    <span style={{
                      color: visibiliteCourbes[entry.dataKey] ? entry.color : '#BFC6CC',
                      textDecoration: visibiliteCourbes[entry.dataKey] ? 'none' : 'line-through',
                      opacity: visibiliteCourbes[entry.dataKey] ? 1 : 0.5
                    }}>
                      {value} {!visibiliteCourbes[entry.dataKey] && '(masqué)'}
                    </span>
                  )}
                />
                <Line type="monotone" dataKey="kW" stroke={visibiliteCourbes.kW ? '#397DFF' : 'transparent'} name="kW" strokeWidth={2} dot={false} />
                {hasReactiveData && (
                  <Line type="monotone" dataKey="kVA" stroke={visibiliteCourbes.kVA ? '#F9C80E' : 'transparent'} name="kVA" strokeWidth={2} dot={false} />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-xs text-[#687175] mt-2">
            💡 Cliquez sur la légende pour masquer/afficher une courbe • {tousLesPoints.length.toLocaleString('fr-FR')} points au total
          </p>
        </div>

        {/* Préconisation de Puissance */}
        <div className="bg-white border border-[#10D183] rounded-lg overflow-hidden">
          <div className="bg-[#10D183] text-white px-4 py-2">
            <h4 className="font-semibold text-sm">Puissances Préconisées par tranche (kVA)</h4>
          </div>
          <div className="p-4 space-y-4">
            <div className="space-y-3">
              <div className={`grid ${plages.length <= 2 ? `grid-cols-${plages.length + 1}` : 'grid-cols-6'} gap-3 text-xs text-[#687175] font-medium`}>
                <div></div>
                {plages.map(plage => <div key={plage} className="text-center">{plage}</div>)}
              </div>
              <div className={`grid ${plages.length <= 2 ? `grid-cols-${plages.length + 1}` : 'grid-cols-6'} gap-3 items-center`}>
                <div className="text-xs text-[#687175]">P. Souscrite</div>
                {plages.map(plage => (
                  <div key={plage} className="text-center">
                    <span className="font-mono text-sm text-[#E53935] font-bold">
                      {puissancesActuellesParPlage[plage] || puissanceSouscrite || '-'}
                    </span>
                  </div>
                ))}
              </div>
              <div className={`grid ${plages.length <= 2 ? `grid-cols-${plages.length + 1}` : 'grid-cols-6'} gap-3 items-center`}>
                <div className="text-xs text-[#10D183] font-medium">P. Préconisée</div>
                {plages.map(plage => (
                  <div key={plage}>
                    <PuissanceInput
                      pdl={pdl}
                      plage={plage}
                      initialValue={precoParPlage[plage]}
                      onSave={handleSavePuissanceParPlage}
                      className="w-full px-2 py-1.5 border border-[#10D183] rounded text-sm font-mono font-bold text-[#10D183] text-center focus:ring-1 focus:ring-[#10D183]"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-[#BFC6CC] pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <label className="text-xs text-[#687175]">P. Courbe (pour affichage) :</label>
                  <PuissanceInput
                    pdl={pdl}
                    plage="courbe"
                    initialValue={precoUniqueCourbe}
                    onSave={handleSavePuissanceCourbe}
                    className="w-24 px-2 py-1 border border-[#BFC6CC] rounded text-sm font-mono text-center focus:ring-1 focus:ring-[#10D183]"
                  />
                  <span className="text-xs text-[#687175]">kVA</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-[#687175] italic">
              💡 Saisissez une puissance par tranche pour le calcul détaillé. Cliquez sur "Actualiser les calculs" pour voir les résultats.
            </p>
          </div>
        </div>

        {/* Consommations par Plage */}
        <div className="bg-white border border-[#397DFF] rounded-lg overflow-hidden">
          <div className="bg-[#397DFF] text-white px-4 py-2 flex items-center justify-between">
            <h4 className="font-semibold text-sm">Consommations annuelles par plage (kWh)</h4>
            <button
              onClick={() => setPlagesHorairesModalPDL(pdl)}
              className="text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-1 rounded flex items-center gap-1 transition-colors"
              data-testid={`btn-plages-horaires-${pdl}`}
            >
              <Clock className="w-3 h-3" />
              {hasCustomPlages ? 'Plages personnalisées' : 'Personnaliser les plages'}
            </button>
          </div>
          <div className="p-4">
            {(() => {
              const totalConso = plages.reduce((sum, p) => sum + (consoActuelleParPlage[p] || 0), 0);
              const facteurAnnuel = 365 / nbJoursUniques;
              return (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-3">
                    {plages.map(plage => (
                      <div key={plage} className="flex-1 min-w-[120px]">
                        <label className="block text-xs text-[#687175] mb-1">{plage}</label>
                        <PuissanceInput
                          pdl={pdl}
                          plage={plage}
                          initialValue={Math.round(consoActuelleParPlage[plage] || 0)}
                          onSave={handleSaveConsommationParPlage}
                          className="w-full px-2 py-1 border border-[#BFC6CC] rounded text-sm font-mono text-right focus:ring-1 focus:ring-[#397DFF]"
                          placeholder="kWh"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-[#BFC6CC]">
                    <span className="text-xs text-[#687175]">
                      Calculé sur {nbJoursUniques} jours (×{facteurAnnuel.toFixed(1)} pour annualiser)
                    </span>
                    <div className="text-right">
                      <span className="text-xs text-[#687175]">Total : </span>
                      <span className="font-mono font-bold text-[#397DFF]">
                        {Math.round(totalConso).toLocaleString('fr-FR')} kWh
                      </span>
                      <span className="text-xs text-[#687175] ml-2">({(totalConso / 1000).toFixed(1)} MWh)</span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Courbe Chronologique */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-semibold text-[#191D1E]">Courbe de Charge avec Comparaison</h4>
              <p className="text-xs text-[#687175]">Puissances maximales agrégées par heure • Ligne rouge = P. Souscrite actuelle • Ligne verte = P. Préconisée</p>
            </div>
            <button
              onClick={() => setZoomReset(prev => prev + 1)}
              className="text-sm text-[#397DFF] hover:text-[#4672B6] px-3 py-1 border border-[#397DFF] rounded"
            >
              🔄 Réinitialiser Zoom
            </button>
          </div>
          <div className="bg-white p-4 rounded-lg border border-[#BFC6CC]">
            <ResponsiveContainer width="100%" height={450}>
              <LineChart
                key={zoomReset}
                data={dataChronologique.map((p, idx) => ({
                  index: idx,
                  horodate: p.horodate,
                  kW: p.kW,
                  kVA: p.kVA,
                  puissanceSouscrite: puissanceSouscrite > 0 ? puissanceSouscrite : null,
                  puissancePreconisee: precoUniqueCourbeValidee > 0 ? precoUniqueCourbeValidee : null,
                }))}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#BFC6CC" />
                <XAxis
                  dataKey="horodate"
                  tickFormatter={(value) => {
                    if (!value) return '';
                    const d = new Date(value);
                    if (isNaN(d.getTime())) return value.substring(0, 10);
                    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
                  }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval="preserveStartEnd"
                  tick={{ fontSize: 10 }}
                />
                <YAxis label={{ value: 'Puissance (kW/kVA)', angle: -90, position: 'insideLeft' }} domain={['auto', 'auto']} />
                <Tooltip
                  labelFormatter={(value) => {
                    if (!value) return '';
                    const d = new Date(value);
                    if (isNaN(d.getTime())) return value;
                    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
                  }}
                  formatter={(value, name) => {
                    if (value === null || value === undefined) return ['-', name];
                    return [typeof value === 'number' ? value.toFixed(2) : value, name];
                  }}
                />
                <Legend
                  onClick={(e) => setVisibiliteCourbes(prev => ({ ...prev, [e.dataKey]: !prev[e.dataKey] }))}
                  wrapperStyle={{ cursor: 'pointer' }}
                  formatter={(value, entry) => {
                    const isVisible = visibiliteCourbes[entry.dataKey];
                    return (
                      <span style={{
                        color: isVisible ? entry.color : '#BFC6CC',
                        textDecoration: isVisible ? 'none' : 'line-through',
                        opacity: isVisible ? 1 : 0.5
                      }}>
                        {value} {!isVisible && '(masqué)'}
                      </span>
                    );
                  }}
                />
                {puissanceSouscrite > 0 && visibiliteCourbes.puissanceSouscrite && (
                  <ReferenceLine y={puissanceSouscrite} stroke="#E53935" strokeDasharray="8 4" strokeWidth={2}
                    label={{ value: `P. Souscrite: ${puissanceSouscrite} kVA`, position: 'insideTopRight', fill: '#E53935', fontSize: 11 }} />
                )}
                {precoUniqueCourbeValidee > 0 && visibiliteCourbes.puissancePreconisee && (
                  <ReferenceLine y={precoUniqueCourbeValidee} stroke="#10D183" strokeDasharray="8 4" strokeWidth={2}
                    label={{ value: `P. Préconisée: ${precoUniqueCourbeValidee} kVA`, position: 'insideBottomRight', fill: '#10D183', fontSize: 11 }} />
                )}
                <Line type="monotone" dataKey="kW" stroke={visibiliteCourbes.kW ? '#397DFF' : 'transparent'} name="kW" strokeWidth={1} dot={false} isAnimationActive={false} />
                {hasReactiveData && (
                  <Line type="monotone" dataKey="kVA" stroke={visibiliteCourbes.kVA ? '#F9C80E' : 'transparent'} name="kVA" strokeWidth={1} dot={false} isAnimationActive={false} />
                )}
                {puissanceSouscrite > 0 && (
                  <Line type="monotone" dataKey="puissanceSouscrite" stroke={visibiliteCourbes.puissanceSouscrite ? '#E53935' : 'transparent'}
                    name="P. Souscrite Actuelle" strokeWidth={2} strokeDasharray="8 4" dot={false} isAnimationActive={false} legendType="plainline" />
                )}
                {precoUniqueCourbeValidee > 0 && (
                  <Line type="monotone" dataKey="puissancePreconisee" stroke={visibiliteCourbes.puissancePreconisee ? '#10D183' : 'transparent'}
                    name="P. Préconisée" strokeWidth={2} strokeDasharray="8 4" dot={false} isAnimationActive={false} legendType="plainline" />
                )}
                <Brush dataKey="index" height={30} stroke="#397DFF" fill="#E8F4FD" />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-xs text-[#687175] mt-2 text-center">
            💡 Utilisez la barre de zoom (en bas) pour sélectionner une période • Cliquez sur la légende pour masquer/afficher • {nbPointsHoraires.toLocaleString('fr-FR')} points horaires
          </p>
        </div>

        {/* Indicateurs Dépassements + Ratio */}
        {calculsAJour && calculsRapport && calculsRapport.pdl === pdl && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
            {formuleActuelle.startsWith('BTINF') ? (
              <div className="p-3 bg-white border border-[#BFC6CC] rounded-lg col-span-2">
                <div className="text-xs text-[#687175] mb-1">Dépassements</div>
                <div className="text-sm text-[#687175] italic">
                  Non applicable en BT Inférieur — le compteur disjoncte en cas de dépassement (pas de CMDPS).
                </div>
              </div>
            ) : (
              <>
                <div className="p-3 bg-white border border-[#E53935]/30 rounded-lg">
                  <div className="text-xs text-[#687175] mb-1">Dépassements (Actuel)</div>
                  <div className="text-lg font-bold text-[#E53935]">
                    {calculsRapport.depassements?.actuel?.heuresUniques || 0} <span className="text-xs font-normal">heures</span>
                  </div>
                  <div className="text-xs text-[#687175]">{calculsRapport.depassements?.actuel?.nbPoints || 0} points</div>
                  <div className="text-sm font-mono mt-1 text-[#E53935]">
                    CMDPS: {(calculsRapport.composantes?.CMDPS?.actuel || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
                  </div>
                </div>
                <div className="p-3 bg-white border border-[#10D183]/30 rounded-lg">
                  <div className="text-xs text-[#687175] mb-1">Dépassements (Proposé)</div>
                  <div className="text-lg font-bold text-[#10D183]">
                    {calculsRapport.depassements?.propose?.heuresUniques || 0} <span className="text-xs font-normal">heures</span>
                  </div>
                  <div className="text-xs text-[#687175]">{calculsRapport.depassements?.propose?.nbPoints || 0} points</div>
                  <div className="text-sm font-mono mt-1 text-[#10D183]">
                    CMDPS: {(calculsRapport.composantes?.CMDPS?.propose || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
                  </div>
                </div>
              </>
            )}
            <div className="p-3 bg-white border border-[#BFC6CC] rounded-lg">
              <div className="text-xs text-[#687175] mb-1">Ratio kWh/kW (Actuel)</div>
              <div className="text-lg font-bold text-[#191D1E]">
                {calculsRapport.puissanceSouscrite > 0
                  ? Math.round(calculsRapport.consoAnnuelleKWh / calculsRapport.puissanceSouscrite).toLocaleString('fr-FR')
                  : '—'}
              </div>
              <div className="text-xs text-[#687175]">
                {Math.round(calculsRapport.consoAnnuelleKWh || 0).toLocaleString('fr-FR')} kWh / {calculsRapport.puissanceSouscrite || 0} kW
              </div>
            </div>
            <div className="p-3 bg-white border border-[#397DFF]/30 rounded-lg">
              <div className="text-xs text-[#687175] mb-1">Ratio kWh/kW (Proposé)</div>
              <div className="text-lg font-bold text-[#397DFF]">
                {calculsRapport.puissanceProposee > 0
                  ? Math.round(calculsRapport.consoAnnuelleKWh / calculsRapport.puissanceProposee).toLocaleString('fr-FR')
                  : '—'}
              </div>
              <div className="text-xs text-[#687175]">
                {Math.round(calculsRapport.consoAnnuelleKWh || 0).toLocaleString('fr-FR')} kWh / {calculsRapport.puissanceProposee || 0} kW
              </div>
            </div>
          </div>
        )}

        {/* Commentaire PDL */}
        <CommentaireInput
          pdl={pdl}
          initialValue={initialCommentaire}
          onSave={handleSaveCommentaire}
        />
      </div>
    </div>
  );
};

export const PDLAnalysisCard = memo(PDLAnalysisCardComponent);
export default PDLAnalysisCard;
